import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';

const LanguageSwitcher = ({ tone = 'default' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { i18n } = useTranslation();
  const dropdownRef = useRef(null);

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'hi', name: 'हिन्दी' },
    { code: 'mr', name: 'मराठी' },
    { code: 'gu', name: 'ગુજરાતી' },
    { code: 'ta', name: 'தமிழ்' },
    { code: 'te', name: 'తెలుగు' },
    { code: 'kn', name: 'ಕನ್ನಡ' }
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleLanguageChange = async (languageCode) => {
    try {
      await i18n?.changeLanguage(languageCode);
      localStorage.setItem('selectedLanguage', languageCode);
      setIsOpen(false);
    } catch (error) {
      console.error('Error changing language:', error);
    }
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const currentLanguage = languages?.find(lang => lang?.code === i18n?.language) || languages?.[0];
  const isDark = tone === 'dark';
  const buttonTone = isDark
    ? 'bg-white/10 hover:bg-white/15 border border-white/35 text-white shadow-sm'
    : 'bg-gradient-to-r from-[#1e4a72]/10 to-[#ff8c00]/10 hover:from-[#1e4a72]/20 hover:to-[#ff8c00]/20 border border-[#1e4a72]/20';
  const iconTone = isDark ? 'text-white' : 'text-[#1e4a72]';

  return (
    <div className="language-switcher flex-shrink-0" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className={`language-switcher-button ${buttonTone} gap-1 sm:gap-2 px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm`}
        aria-label="Select language"
        aria-expanded={isOpen}
      >
        <Icon name="Globe" size={14} className={`${iconTone} flex-shrink-0`} />
        <span className={`font-semibold whitespace-nowrap ${iconTone}`}>{currentLanguage?.name}</span>
        <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={16} className={`${iconTone} flex-shrink-0 hidden sm:block`} />
      </button>
      {isOpen && (
        <div className="language-switcher-dropdown">
          {languages?.map((language) => (
            <button
              key={language?.code}
              onClick={() => handleLanguageChange(language?.code)}
              className={`language-switcher-option ${
                i18n?.language === language?.code ? 'active' : ''
              }`}
            >
              <span>{language?.name}</span>
              {i18n?.language === language?.code && (
                <Icon name="Check" size={16} className="ml-auto text-[#ff8c00]" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
